const GEO_WEATHER_API_URL = "https://geow.adctv.com";

export function mergeContextObjects(baseValue, incomingValue) {
    const baseIsObject = baseValue && typeof baseValue === "object" && !Array.isArray(baseValue);
    const incomingIsObject = incomingValue && typeof incomingValue === "object" && !Array.isArray(incomingValue);

    if (!incomingIsObject) {
        return incomingValue;
    }

    const merged = baseIsObject ? { ...baseValue } : {};
    Object.keys(incomingValue).forEach((key) => {
        merged[key] = mergeContextObjects(merged[key], incomingValue[key]);
    });

    return merged;
}

function getDeviceOs(userAgent) {
    if (/iphone|ipad|ipod/i.test(userAgent)) return "ios";
    if (/android/i.test(userAgent)) return "android";
    if (/windows/i.test(userAgent)) return "windows";
    if (/mac os x|macintosh/i.test(userAgent)) return "macos";
    if (/linux/i.test(userAgent)) return "linux";
    return "unknown";
}

function getDeviceBrowser(userAgent) {
    if (/edg\//i.test(userAgent)) return "edge";
    if (/opr\//i.test(userAgent) || /opera/i.test(userAgent)) return "opera";
    if (/firefox/i.test(userAgent)) return "firefox";
    if (/chrome|crios/i.test(userAgent) && !/edg\//i.test(userAgent) && !/opr\//i.test(userAgent)) return "chrome";
    if (/safari/i.test(userAgent) && !/chrome|crios|edg\//i.test(userAgent)) return "safari";
    return "unknown";
}

function getScreenSizeTier(width) {
    if (width < 480) return "small";
    if (width <= 768) return "medium";
    if (width <= 1440) return "large";
    return "xl";
}

function getViewportSizeTier(width) {
    if (width < 480) return "xs";
    if (width <= 640) return "sm";
    if (width <= 768) return "md";
    if (width <= 1024) return "lg";
    return "xl";
}

export function buildDerivedContext() {
    const userAgent = (typeof navigator !== "undefined" ? navigator.userAgent : "").toLowerCase();
    const viewportWidth = (typeof window !== "undefined" ? window.innerWidth : 0)
        || (typeof document !== "undefined" ? document.documentElement.clientWidth : 0)
        || (typeof screen !== "undefined" ? screen.width : 0);
    const viewportHeight = (typeof window !== "undefined" ? window.innerHeight : 0)
        || (typeof document !== "undefined" ? document.documentElement.clientHeight : 0)
        || (typeof screen !== "undefined" ? screen.height : 0);
    const screenWidth = (typeof window !== "undefined" && window.screen?.width) || viewportWidth;
    const orientation = viewportWidth > viewportHeight ? "landscape" : "portrait";
    const isTv = /smart-tv|smarttv|googletv|appletv|hbbtv|netcast|viera|tizen|web0s|webos|roku|aftb|aftm|bravia|crkey|tv/i.test(userAgent);
    const isTablet = !isTv && (/ipad|tablet/i.test(userAgent) || (/android/i.test(userAgent) && viewportWidth >= 768 && viewportWidth <= 1024));
    const isMobile = !isTv && !isTablet && (/mobi|iphone|ipod|android/i.test(userAgent) || viewportWidth < 768);
    const deviceType = isTv ? "tv" : isTablet ? "tablet" : isMobile ? "mobile" : "desktop";

    return {
        device: {
            type: deviceType,
            is_mobile: isMobile,
            is_tablet: isTablet,
            is_desktop: !isMobile && !isTablet && !isTv,
            os: getDeviceOs(userAgent),
            browser: getDeviceBrowser(userAgent),
            orientation,
            screen_size_tier: getScreenSizeTier(screenWidth),
        },
        viewport: {
            size_tier: getViewportSizeTier(viewportWidth),
            orientation,
            width: viewportWidth,
            height: viewportHeight,
        },
    };
}

export function ensureDerivedContext() {
    const existingContext = window.__ADCTVContext__ && typeof window.__ADCTVContext__ === "object"
        ? window.__ADCTVContext__
        : {};

    window.__ADCTVContext__ = mergeContextObjects(existingContext, buildDerivedContext());
}

export function isPlainObject(value) {
    return value && typeof value === "object" && !Array.isArray(value);
}

/** Same usable-geo gate as tracker.js / tag.ins (lat or country present). */
export function hasUsableGeoContext(context = (typeof window !== "undefined" ? window.__ADCTVContext__ : undefined)) {
    const geo = isPlainObject(context) ? context.geo : null;
    return !!(isPlainObject(geo) && (geo.lat != null || geo.country != null));
}

export function hasUsableWeatherContext(context = (typeof window !== "undefined" ? window.__ADCTVContext__ : undefined)) {
    const weather = isPlainObject(context) ? context.weather : null;
    return !!(isPlainObject(weather) && (weather.temp != null || weather.condition != null));
}

export function containsWeatherContext(value) {
    if (!value) return false;
    if (typeof value === "string") {
        return /{{\s*(?:weather|geoweather)\.[\w.]+\s*}}/i.test(value)
            || /\bweather\.(temp|condition|humidity|feels_like|wind_speed|uv_index|icon|text|sunrise|sunset)/i.test(value);
    }
    if (typeof value === "object") {
        try {
            const str = JSON.stringify(value);
            return /{{\s*(?:weather|geoweather)\.[\w.]+\s*}}/i.test(str)
                || /\bweather\.(temp|condition|humidity|feels_like|wind_speed|uv_index|icon|text|sunrise|sunset)/i.test(str);
        } catch (e) {
            return false;
        }
    }
    return false;
}

export function isWeatherContextRequired(creativeOrFlag = null) {
    if (creativeOrFlag === true || creativeOrFlag === "true") {
        return true;
    }
    if (typeof creativeOrFlag === "string") {
        if (/data-has-weather-context-in-creative=["']true["']/i.test(creativeOrFlag)) return true;
        if (/[?&]weather=true\b/i.test(creativeOrFlag)) return true;
        if (containsWeatherContext(creativeOrFlag)) return true;
    }
    if (creativeOrFlag && typeof creativeOrFlag === "object") {
        if (creativeOrFlag.has_weather_context_in_creative === true || creativeOrFlag.has_weather_context_in_creative === "true"
            || creativeOrFlag.hasWeatherContextInCreative === true || creativeOrFlag.hasWeatherContextInCreative === "true"
            || creativeOrFlag.hasWeatherContext === true || creativeOrFlag.hasWeatherContext === "true"
            || creativeOrFlag.weather === true || creativeOrFlag.weather === "true") {
            return true;
        }
        if (creativeOrFlag.details && (
            creativeOrFlag.details.has_weather_context_in_creative === true ||
            creativeOrFlag.details.has_weather_context_in_creative === "true" ||
            creativeOrFlag.details.hasWeatherContextInCreative === true ||
            creativeOrFlag.details.hasWeatherContextInCreative === "true"
        )) {
            return true;
        }
        if (creativeOrFlag.creativeSettings && (
            creativeOrFlag.creativeSettings.has_weather_context_in_creative === true ||
            creativeOrFlag.creativeSettings.has_weather_context_in_creative === "true" ||
            creativeOrFlag.creativeSettings.hasWeatherContextInCreative === true ||
            creativeOrFlag.creativeSettings.hasWeatherContextInCreative === "true"
        )) {
            return true;
        }
        if (creativeOrFlag.vastTag && (
            creativeOrFlag.vastTag.has_weather_context_in_creative === true ||
            creativeOrFlag.vastTag.has_weather_context_in_creative === "true" ||
            (typeof creativeOrFlag.vastTag.html === "string" && /[?&]weather=true\b/i.test(creativeOrFlag.vastTag.html))
        )) {
            return true;
        }
        if (typeof creativeOrFlag.insTag === "string" && (
            /data-has-weather-context-in-creative=["']true["']/i.test(creativeOrFlag.insTag) ||
            /[?&]weather=true\b/i.test(creativeOrFlag.insTag) ||
            containsWeatherContext(creativeOrFlag.insTag)
        )) {
            return true;
        }
        if (typeof creativeOrFlag.versionHtml === "string" && (
            /data-has-weather-context-in-creative=["']true["']/i.test(creativeOrFlag.versionHtml) ||
            /[?&]weather=true\b/i.test(creativeOrFlag.versionHtml) ||
            containsWeatherContext(creativeOrFlag.versionHtml)
        )) {
            return true;
        }
        if (containsWeatherContext(creativeOrFlag)) {
            return true;
        }
    }

    try {
        const searchStr = (typeof window !== "undefined" && window.location && window.location.search)
            ? window.location.search
            : ((typeof location !== "undefined" && location && location.search) ? location.search : "");
        if (searchStr) {
            const params = new URLSearchParams(searchStr);
            const weatherParam = params.get("weather");
            const hasWeatherParam = params.get("has_weather_context_in_creative")
                || params.get("hasWeatherContextInCreative")
                || params.get("hasWeatherContext");
            if (weatherParam === "true" || hasWeatherParam === "true") {
                return true;
            }
        }
    } catch (e) {}

    try {
        if (typeof window !== "undefined") {
            if (window.has_weather_context_in_creative === true || window.has_weather_context_in_creative === "true"
                || window.hasWeatherContextInCreative === true || window.hasWeatherContextInCreative === "true"
                || window.hasWeatherContext === true || window.hasWeatherContext === "true") {
                return true;
            }
            const cs = window.creativeSettings || window.extraCreativeSettings || window.settings;
            if (cs && (cs.has_weather_context_in_creative === true || cs.has_weather_context_in_creative === "true"
                || cs.hasWeatherContextInCreative === true || cs.hasWeatherContextInCreative === "true")) {
                return true;
            }
            if (window.macroData && (
                window.macroData.has_weather_context_in_creative === true || window.macroData.has_weather_context_in_creative === "true" ||
                (window.macroData.data && (window.macroData.data.has_weather_context_in_creative === true || window.macroData.data.has_weather_context_in_creative === "true"))
            )) {
                return true;
            }
            if (window.model) {
                if (window.model.creativeSettings && (
                    window.model.creativeSettings.has_weather_context_in_creative === true ||
                    window.model.creativeSettings.has_weather_context_in_creative === "true"
                )) {
                    return true;
                }
                if (window.model.has_weather_context_in_creative === true || window.model.has_weather_context_in_creative === "true") {
                    return true;
                }
                if (containsWeatherContext(window.model)) {
                    return true;
                }
            }
            if (window.adctvContextData && containsWeatherContext(window.adctvContextData)) {
                return true;
            }
        }
    } catch (e) {}

    try {
        if (typeof window !== "undefined" && window.parent && window.parent !== window) {
            const parent = window.parent;
            if (parent.__adctvHasWeatherRequested === true) {
                return true;
            }
            if (parent.has_weather_context_in_creative === true || parent.has_weather_context_in_creative === "true"
                || parent.hasWeatherContextInCreative === true || parent.hasWeatherContextInCreative === "true"
                || parent.hasWeatherContext === true || parent.hasWeatherContext === "true") {
                return true;
            }
            const pcs = parent.creativeSettings || parent.extraCreativeSettings || parent.settings;
            if (pcs && (pcs.has_weather_context_in_creative === true || pcs.has_weather_context_in_creative === "true"
                || pcs.hasWeatherContextInCreative === true || pcs.hasWeatherContextInCreative === "true")) {
                return true;
            }
            if (parent.location && parent.location.search) {
                const pParams = new URLSearchParams(parent.location.search);
                if (pParams.get("weather") === "true" || pParams.get("has_weather_context_in_creative") === "true") {
                    return true;
                }
            }
        }
    } catch (e) {}

    return false;
}

function getFallbackGeoContext() {
    return {
        geo: { country: null, city: null, region: null, lat: null, lng: null, timezone: null },
        network: { isp: null, asn: null, protocol: null, rtt: null, speed_tier: null, supports_rich_media: null, video_quality_tier: null },
        bot: { score: null, is_bot: null, is_corporate_proxy: null },
        time: { timestamp: null, hour: null, day_of_week: null, timezone: null, daypart: null, is_weekend: null, is_work_hours: null, is_commuting_hours: null, meal_period: null, productivity_state: null },
        weather: { temp: null, feels_like: null, local_hour: null, humidity: null, condition: null, icon: "//wcdn.airtory.com/weather/64x64/day/116.png", text: null, is_day: null, precip: null, wind_speed: null, uv_index: null, sunrise: null, sunset: null, visibility: null, temperature_band: null, condition_type: null, uv_level: null, comfort_level: null, wind_level: null, is_daylight: null, precipitation_level: null, visibility_level: null, activity_suitability: null },
        audience: { outdoor_score: null, outdoor_likelihood: null, commerce_intent: null, engagement_likelihood: null, mobility_state: null, session_mood: null, purchase_context: null },
        traffic: { quality_tier: null, is_suspicious: null, trust_score: null },
        device: { rich_media_ready: null, experience_tier: null, type: null, is_mobile: null, is_tablet: null, is_desktop: null, os: null, browser: null, orientation: null, screen_size_tier: null },
        viewport: { size_tier: null, orientation: null },
        media: { video_quality: null },
    };
}

function dispatchContextReady() {
    if (typeof document !== "undefined") {
        document.dispatchEvent(new CustomEvent("adctv-context-ready", { bubbles: true }));
    }
}

/**
 * Fetches geo/weather context once and stores it on window.__ADCTVContext__.
 * Mirrors App.vue behavior so preview iframes can reuse the preloaded context.
 *
 * Non-blocking contract: callers MUST NOT await this before creative / Preview init.
 * Fire-and-forget is correct; the returned / __adctvGeoFetchPromise can be observed later.
 * Failures always resolve (never reject) so there is no unhandledrejection.
 */
export function preloadGeoContext(creativeOrOptions = null) {
    ensureDerivedContext();

    const isWeatherRequired = isWeatherContextRequired(creativeOrOptions);

    // Single-flight: return the in-flight promise if weather condition is satisfied.
    const shouldReuseInFlight = window.__adctvGeoFetchPromise && (!isWeatherRequired || window.__adctvHasWeatherRequested);
    if (shouldReuseInFlight) {
        window.geoApiRendered = true;
        return window.__adctvGeoFetchPromise;
    }

    const shouldResolveFromCache = hasUsableGeoContext() && (!isWeatherRequired || hasUsableWeatherContext());
    if (shouldResolveFromCache) {
        window.geoApiRendered = true;
        return Promise.resolve(window.__ADCTVContext__);
    }

    window.geoApiRendered = true;
    window.__adctvHasWeatherRequested = Boolean(window.__adctvHasWeatherRequested || isWeatherRequired);

    const apiUrl = isWeatherRequired ? `${GEO_WEATHER_API_URL}?weather=true` : GEO_WEATHER_API_URL;

    window.__adctvGeoFetchPromise = fetch(apiUrl)
        .then((response) => response.text())
        .then((scriptContent) => {
            const fetchedContext = scriptContent ? JSON.parse(scriptContent)?.context : {};
            const existingContext = window.__ADCTVContext__ && typeof window.__ADCTVContext__ === "object"
                ? window.__ADCTVContext__
                : {};
            window.__ADCTVContext__ = mergeContextObjects(
                existingContext,
                fetchedContext && typeof fetchedContext === "object" ? fetchedContext : {}
            );
            ensureDerivedContext();
            dispatchContextReady();
            return window.__ADCTVContext__;
        })
        .catch((error) => {
            console.error("Error loading Geo Weather API:", error);
            const existingContext = window.__ADCTVContext__ && typeof window.__ADCTVContext__ === "object"
                ? window.__ADCTVContext__
                : {};
            window.__ADCTVContext__ = mergeContextObjects(existingContext, getFallbackGeoContext());
            ensureDerivedContext();
            dispatchContextReady();
            return window.__ADCTVContext__;
        });

    return window.__adctvGeoFetchPromise;
}
